const defineNewsletterModal = () => {
  if (customElements.get('newsletter-modal')) return;

  customElements.define(
    'newsletter-modal',
    class NewsletterModal extends ModalElement {
      constructor() {
        super();
    
        // Prevent popup on Shopify robot challenge page
        if (window.location.pathname === '/challenge' || !theme.cookiesEnabled) {
          return;
        }
    
        if (!theme.config.isTouch || Shopify.designMode) {
          this.init();
        }
        else {
          new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
        }
      }
    
      get shouldLock() {
        return true;
      }

      connectedCallback() {
        super.connectedCallback();
        this.addEventListener('click', this.onDiscountCopyClick.bind(this), { signal: this.abortController.signal });
        this.addEventListener('submit', this.onNewsletterSubmit.bind(this), { capture: true, signal: this.abortController.signal });
      }
    
      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }
    
      get delay() {
        return this.hasAttribute('data-delay') ? parseInt(this.getAttribute('data-delay')) : 5;
      }
    
      get expiry() {
        return this.hasAttribute('data-expiry') ? parseInt(this.getAttribute('data-expiry')) : 30;
      }
    
      get cookieName() {
        return 'concept:newsletter-popup';
      }

      get successSessionName() {
        return 'concept:newsletter-popup-success';
      }

      get pendingSessionName() {
        return 'concept:newsletter-popup-pending';
      }

      get returnedFromSignup() {
        return new URLSearchParams(window.location.search).get('newsletter_popup_posted') === 'true';
      }

      get hasSessionSuccess() {
        try {
          return window.sessionStorage.getItem(this.successSessionName) === 'true';
        }
        catch (error) {
          return false;
        }
      }

      get hasPendingSuccess() {
        try {
          return window.sessionStorage.getItem(this.pendingSessionName) === 'true';
        }
        catch (error) {
          return false;
        }
      }
    
      get submited() {
        return this.querySelector('.newsletter-form .alert') !== null || this.querySelector('[data-newsletter-success]:not([hidden])') !== null;
      }
    
      init() {
        if (this.initialized) return;
        this.initialized = true;

        if (this.returnedFromSignup) {
          this.showSuccessState(true);
          this.load(1);
          return;
        }

        if (this.hasPendingSuccess) {
          this.showSuccessState(true);
          this.load(1);
          return;
        }

        if (this.hasSessionSuccess) {
          this.showSuccessState();
          return;
        }
        
        // Open modal if errors or success message exist
        if (this.submited) {
          this.load(1);
          return;
        }
    
        if (this.testMode || !this.getCookie(this.cookieName)) {
          this.load(this.delay);
        }
      }
    
      load(delay) {
        if (Shopify.designMode) return;
    
        setTimeout(() => this.show(), delay * 1000);
      }

      showTransition() {
        this.setAttribute('active', '');
        return Promise.resolve();
      }

      hideTransition() {
        this.removeAttribute('active');
        return Promise.resolve();
      }

      onPanStart() {
        /* Center-card popup: no swipe-to-dismiss; close control only. */
      }

      onPanMove() {
        /* Intentionally empty — disables bottom-sheet drag from ModalElement. */
      }

      onPanEnd() {
        if (this.gestureConfig?.animationFrame != null) {
          cancelAnimationFrame(this.gestureConfig.animationFrame);
          this.gestureConfig.animationFrame = null;
        }
      }

      onDiscountCopyClick(event) {
        const button = event.target.closest('[data-newsletter-copy-code]');
        if (!button) return;

        const code = button.getAttribute('data-code');
        if (!code) return;

        const copyLabel = button.getAttribute('data-copy-label') || button.textContent;
        const copiedLabel = button.getAttribute('data-copied-label') || copyLabel;
        const setCopiedState = () => {
          button.textContent = copiedLabel;
          button.dataset.copied = 'true';

          setTimeout(() => {
            button.textContent = copyLabel;
            delete button.dataset.copied;
          }, 2000);
        };
        const fallbackCopy = () => {
          const textarea = document.createElement('textarea');
          textarea.value = code;
          textarea.setAttribute('readonly', '');
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          textarea.remove();
          setCopiedState();
        };

        if (navigator.clipboard) {
          navigator.clipboard.writeText(code).then(setCopiedState).catch(fallbackCopy);
          return;
        }

        fallbackCopy();
      }

      onNewsletterSubmit(event) {
        if (!event.target.closest('.newsletter-form--popup')) return;

        try {
          window.sessionStorage.setItem(this.pendingSessionName, 'true');
        }
        catch (error) {
          // Native Shopify submission still works when sessionStorage is unavailable.
        }
      }

      showSuccessState(persist = false) {
        const successElement = this.querySelector('[data-newsletter-success]');
        if (!successElement) return;

        this.classList.add('newsletter-modal--submitted');
        successElement.hidden = false;

        if (persist) {
          try {
            window.sessionStorage.setItem(this.successSessionName, 'true');
            window.sessionStorage.removeItem(this.pendingSessionName);
          }
          catch (error) {
            // Session persistence is best-effort.
          }
        }
      }
    
      afterShow() {
        super.afterShow();

        if (this.querySelector('[data-newsletter-success]:not([hidden])')) {
          this.classList.add('newsletter-modal--submitted');
        }
      }
    
      afterHide() {
        super.afterHide();
    
        // Remove a cookie in case it was set in test mode
        if (this.testMode) {
          this.removeCookie(this.cookieName);
          return;
        }
    
        this.setCookie(this.cookieName, this.expiry);
      }
    
      getCookie(name) {
        const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
        return match ? match[2] : null;
      }
    
      setCookie(name, expiry) {
        document.cookie = `${name}=true; max-age=${(expiry * 24 * 60 * 60)}; path=/`;
      }
    
      removeCookie(name) {
        document.cookie = `${name}=; max-age=0`;
      }
    }
  );
};

if (customElements.get('modal-element')) {
  defineNewsletterModal();
}
else {
  customElements.whenDefined('modal-element').then(defineNewsletterModal);
}
